import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
    propTypes,
    LISTING_STATE_CLOSED,
    LISTING_STATE_PENDING_APPROVAL,
    LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { ensureOwnListing } from '../../util/data';
import { resolveActivitiesSlug } from '../../util/urlHelpers';
import { capitalize } from '../../util/text';
import SharingViaSocialNetworkModal from '../../containers/ListingPage/components/SharingViaSocialNetworkModal';
import CloseListingModal from './components/CloseListingModal';
import DeleteDraftModal from './components/DeleteDraftModal';
import ListingControls from './components/ListingControls';
import ListingMedia from './components/ListingMedia';
import ListingInfo from './components/ListingInfo';
import { useListingCardActions } from './useListingCardActions';
import { ManageListingCardContext } from './ManageListingCardContext';
import { MODAL_DELETE_DRAFT, MODAL_PAUSE, MODAL_SHARE } from './constants';

import css from './ManageListingCard.css';

const NAVIGABLE_STATES = [
    LISTING_STATE_PUBLISHED,
    LISTING_STATE_PENDING_APPROVAL,
    LISTING_STATE_CLOSED,
];

export const ManageListingCard = ({
    className,
    rootClassName,
    controlPanelIndex /** index of ML card */,
    activeControlPanelIndex,
    setActiveControlPanelIndex,
    listing,
}) => {
    const [activeModal, setActiveModal] = useState(null);
    const closeModal = () => setActiveModal(null);

    const currentListing = ensureOwnListing(listing);
    const actions = useListingCardActions(currentListing);

    const { publicData, title = '', state } = currentListing.attributes;
    const { addressDetails = {}, activities } = publicData;
    const { postalCode, location } = addressDetails;

    const listingSeoActivity = capitalize(resolveActivitiesSlug(activities));
    const sharingDescription = `${listingSeoActivity} gesucht für ${title} in ${postalCode} ${location}! 🐴💜`;

    const navigationAllowed = NAVIGABLE_STATES.includes(state);
    const controlsOpened = controlPanelIndex === activeControlPanelIndex;

    const contextValue = { listing: currentListing, actions, openModal: setActiveModal };

    const classes = classNames(rootClassName || css.root, {
        [className]: !!className,
        [css.navigationAllowed]: navigationAllowed,
    });

    return (
        <ManageListingCardContext.Provider value={contextValue}>
            {activeModal === MODAL_PAUSE && (
                <CloseListingModal
                    onSubmit={values => {
                        actions.pauseListing(values);
                        closeModal();
                    }}
                    onClose={closeModal}
                />
            )}
            {activeModal === MODAL_SHARE && (
                <SharingViaSocialNetworkModal
                    isOpen
                    listing={currentListing}
                    emailSharingDescription={sharingDescription}
                    onCloseEnquiryModal={closeModal}
                />
            )}
            {activeModal === MODAL_DELETE_DRAFT && (
                <DeleteDraftModal
                    onClose={closeModal}
                    onConfirm={() => {
                        actions.discardDraft();
                        closeModal();
                    }}
                />
            )}
            <div className={classes}>
                <ListingControls
                    isOpened={controlsOpened}
                    onToggle={() =>
                        setActiveControlPanelIndex(controlsOpened ? -1 : controlPanelIndex)
                    }
                    onDismiss={() => setActiveControlPanelIndex(-1)}
                />
                <div onClick={navigationAllowed ? actions.navigateToListingPage : undefined}>
                    <ListingMedia />
                    <ListingInfo />
                </div>
            </div>
        </ManageListingCardContext.Provider>
    );
};

ManageListingCard.propTypes = {
    className: PropTypes.string,
    rootClassName: PropTypes.string,
    controlPanelIndex: PropTypes.number.isRequired,
    activeControlPanelIndex: PropTypes.number.isRequired,
    setActiveControlPanelIndex: PropTypes.func.isRequired,
    listing: propTypes.ownListing.isRequired,
};

export default ManageListingCard;
